import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useManagerAuth } from '../context/ManagerAuthContext';
import {
  Eye, EyeOff, Lock, Shield, Loader2, ArrowRight, X, ShoppingBag,
  BarChart3, Package, Users, Warehouse, CheckCircle2, Sparkles
} from 'lucide-react';

const StaffLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login: adminLogin } = useAdminAuth();
  const { login: managerLogin } = useManagerAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!password.trim()) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    try {
      // Try admin login first
      try {
        await adminLogin(email, password);
        navigate('/admin/dashboard');
        return;
      } catch (adminError) {
        // If admin login fails, try manager login
        console.log('Admin login failed, trying manager login...');
      }

      // Try manager login
      try {
        await managerLogin(email, password);
        navigate('/manager/dashboard');
        return;
      } catch (managerError) {
        // Both failed
        setError('Invalid credentials or access denied');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex font-sans relative overflow-hidden">
      
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Diagonal Cross Grid Pattern - Animated */}
        <div className="absolute inset-0 opacity-[0.04] animate-[drift_20s_ease-in-out_infinite]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cross-pattern-staff" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                {/* Diagonal line top-left to bottom-right */}
                <line x1="0" y1="0" x2="40" y2="40" stroke="currentColor" strokeWidth="1.5" className="text-slate-900" />
                {/* Diagonal line top-right to bottom-left */}
                <line x1="40" y1="0" x2="0" y2="40" stroke="currentColor" strokeWidth="1.5" className="text-slate-900" />
                {/* Horizontal line */}
                <line x1="0" y1="20" x2="40" y2="20" stroke="currentColor" strokeWidth="0.5" className="text-blue-500" />
                {/* Vertical line */}
                <line x1="20" y1="0" x2="20" y2="40" stroke="currentColor" strokeWidth="0.5" className="text-indigo-500" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cross-pattern-staff)" />
          </svg>
        </div>
        
        {/* Gradient Orbs with Glow - Floating */}
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-gradient-to-br from-blue-400/30 via-indigo-500/20 to-purple-500/10 rounded-full blur-3xl animate-[float_8s_ease-in-out_infinite]" 
             style={{ boxShadow: '0 0 200px rgba(59, 130, 246, 0.3)' }} />
        <div className="absolute bottom-[-20%] left-[-10%] w-[700px] h-[700px] bg-gradient-to-tr from-orange-400/30 via-amber-500/20 to-orange-600/10 rounded-full blur-3xl animate-[float_10s_ease-in-out_infinite]" 
             style={{ boxShadow: '0 0 250px rgba(251, 146, 60, 0.3)', animationDelay: '1s' }} />
        
        {/* Animated Accent Lines - Moving */}
        <div className="absolute top-1/4 left-0 w-32 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-40 animate-[slideRight_6s_ease-in-out_infinite]" />
        <div className="absolute top-1/3 right-0 w-40 h-0.5 bg-gradient-to-l from-transparent via-indigo-500 to-transparent opacity-40 animate-[slideLeft_7s_ease-in-out_infinite]" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-1/4 left-1/4 w-24 h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-40 animate-[slideRight_5s_ease-in-out_infinite]" style={{ animationDelay: '1.5s' }} />
        
        {/* Floating Geometric Shapes with Gradients */}
        <div className="absolute top-20 left-20 w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-60 shadow-lg shadow-blue-500/50 animate-[bounce_3s_ease-in-out_infinite]" />
        <div className="absolute top-40 right-40 w-4 h-4 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 opacity-50 shadow-lg shadow-orange-500/50 animate-[float_4s_ease-in-out_infinite]" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-32 right-1/4 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 opacity-50 shadow-lg shadow-indigo-500/50 animate-[float_3.5s_ease-in-out_infinite]" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/3 left-1/3 w-3 h-3 rotate-45 bg-gradient-to-br from-teal-500 to-emerald-600 opacity-40 shadow-lg shadow-teal-500/40 animate-[spin_8s_linear_infinite]" />
        
        {/* Curved Accent Lines with Gradient - Animated */}
        <svg className="absolute top-10 right-10 opacity-20 animate-[float_6s_ease-in-out_infinite]" width="200" height="200" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="curve-gradient-staff-1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0" />
              <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M 0 100 Q 50 50, 100 100 T 200 100" stroke="url(#curve-gradient-staff-1)" strokeWidth="2" fill="none" />
        </svg>
        
        <svg className="absolute bottom-10 left-10 opacity-20 animate-[float_7s_ease-in-out_infinite]" width="200" height="200" viewBox="0 0 200 200" style={{ animationDelay: '1s' }}>
          <defs>
            <linearGradient id="curve-gradient-staff-2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0" />
              <stop offset="50%" stopColor="#f97316" stopOpacity="1" />
              <stop offset="100%" stopColor="#fb923c" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M 0 100 Q 50 150, 100 100 T 200 100" stroke="url(#curve-gradient-staff-2)" strokeWidth="2" fill="none" />
        </svg>
      </div>
      
      {/* Custom Keyframe Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(10px, 10px); }
        }
        @keyframes slideRight {
          0%, 100% { transform: translateX(-50px); opacity: 0; }
          50% { transform: translateX(50px); opacity: 0.4; }
        }
        @keyframes slideLeft {
          0%, 100% { transform: translateX(50px); opacity: 0; }
          50% { transform: translateX(-50px); opacity: 0.4; }
        }
      `}</style>

      {/* Left Panel - Brand & Features */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 p-16 flex-col justify-between">
        <div>
          {/* Logo & Brand */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logoicon.png" 
                alt="BrightBazaar Icon" 
                className="h-12 w-auto"
              />
              <div>
                <div className="text-2xl font-black text-slate-900 tracking-tight">
                  BRIGHT<span className="text-orange-600">BAZAAR</span>
                </div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Staff Management System
                </div>
              </div>
            </div>
            
            <div className="space-y-1">
              <h1 className="text-5xl font-black text-slate-900 leading-tight tracking-tight">
                Welcome to<br />Admin Portal
              </h1>
              <p className="text-slate-600 text-lg font-medium max-w-md">
                Secure access for administrators and warehouse managers
              </p>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 gap-4 max-w-xl">
            {[
              { icon: BarChart3, label: 'Real-time Analytics', desc: 'Track performance metrics', color: 'from-blue-500 to-blue-600' },
              { icon: Package, label: 'Inventory Control', desc: 'Manage stock levels', color: 'from-emerald-500 to-emerald-600' },
              { icon: Users, label: 'Customer Insights', desc: 'View customer data', color: 'from-purple-500 to-purple-600' },
              { icon: Warehouse, label: 'Warehouse Ops', desc: 'Coordinate logistics', color: 'from-orange-500 to-orange-600' }
            ].map((item, i) => (
              <div key={i} className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-xl hover:shadow-slate-900/5 hover:border-slate-300 transition-all duration-300">
                <div className={`w-10 h-10 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                  <item.icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="text-sm font-black text-slate-900 mb-1">{item.label}</div>
                <div className="text-[11px] text-slate-500 font-medium">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="flex items-center gap-8 text-xs font-bold text-slate-500">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" strokeWidth={2.5} />
            <span>256-bit SSL Encrypted</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500" strokeWidth={2.5} />
            <span>Secure Authentication</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 relative z-10 flex items-center justify-center p-8 lg:p-16">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-8 right-8 w-10 h-10 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-900 transition-all group"
          title="Back to Home"
        >
          <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
        </button>

        {/* Login Card */}
        <div className="w-full max-w-md">
          {/* Logo - Outside and Above the Card */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <img 
              src="/images/logoicon.png" 
              alt="BrightBazaar Icon" 
              className="h-16 w-auto"
            />
            <img 
              src="/images/logotext.png" 
              alt="BrightBazaar" 
              className="h-14 w-auto"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-10 shadow-2xl shadow-slate-900/10">
            
            {/* Header */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl px-4 py-2 mb-4">
                <Sparkles className="w-4 h-4 text-orange-600" />
                <span className="text-xs font-black text-orange-900 uppercase tracking-wider">Admin Access</span>
              </div>
              
              <h2 className="text-3xl font-black text-slate-900 mb-2 tracking-tight">
                Sign In
              </h2>
              <p className="text-slate-600 text-sm font-medium">
                Enter your credentials to access the dashboard
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              
              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all font-medium"
                  placeholder="Enter your email address"
                  required
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3.5 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 transition-all font-medium"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 animate-in slide-in-from-top-2 duration-300">
                  <p className="text-red-700 text-sm font-bold flex items-center gap-2">
                    <Lock className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-black rounded-xl transition-all duration-300 uppercase tracking-wider text-sm shadow-lg shadow-orange-600/30 hover:shadow-orange-600/50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group mt-6"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In to Dashboard
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Bottom Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500 font-medium">
              © 2026{' '}
              <a 
                href="https://brightplanetventures.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-orange-600 transition-colors underline decoration-dotted"
              >
                BrightPlanet Ventures
              </a>
              {' '}and{' '}
              <a 
                href="https://brightbazaar.in/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-orange-600 transition-colors underline decoration-dotted"
              >
                BrightBazaar
              </a>
              . All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
